import { derivePositionPnl } from './broker/normalizedPnl'

export const CHART_COLORS = [
  '#4a6741', '#6aaa5e', '#5b7ea1', '#c4953a', '#7c6bb5',
  '#4a9c8c', '#d4764e', '#b83a2e', '#e05a4a', '#8a92a0',
]

const INSTRUMENT_GROUPS = [
  { key: 'equities', label: 'Equities', types: ['EQUITY'] },
  { key: 'etfs', label: 'ETFs & Funds', types: ['COLLECTIVE_INVESTMENT', 'MUTUAL_FUND'] },
  { key: 'options', label: 'Options', types: ['OPTION'] },
  { key: 'fixedIncome', label: 'Fixed Income', types: ['FIXED_INCOME'] },
  { key: 'cash', label: 'Cash & Money Market', types: ['CASH_EQUIVALENT', 'CURRENCY'] },
]

const SECTOR_MAP = {
  AAPL: 'Technology', MSFT: 'Technology', NVDA: 'Technology', AMD: 'Technology',
  GOOGL: 'Technology', GOOG: 'Technology', META: 'Technology', AVGO: 'Technology',
  CRM: 'Technology', ADBE: 'Technology', INTC: 'Technology', CSCO: 'Technology',
  ORCL: 'Technology', NOW: 'Technology', UBER: 'Technology', SHOP: 'Technology',
  SQ: 'Technology', PLTR: 'Technology', CRWD: 'Technology', SNOW: 'Technology',
  NET: 'Technology', DDOG: 'Technology', MDB: 'Technology', COIN: 'Technology',
  SOFI: 'Technology', SNAP: 'Technology', PINS: 'Technology', RBLX: 'Technology',
  AMZN: 'Consumer Discretionary', TSLA: 'Consumer Discretionary',
  HD: 'Consumer Discretionary', NKE: 'Consumer Discretionary',
  SBUX: 'Consumer Discretionary', TGT: 'Consumer Discretionary',
  LOW: 'Consumer Discretionary', BKNG: 'Consumer Discretionary',
  ABNB: 'Consumer Discretionary', DIS: 'Consumer Discretionary',
  NFLX: 'Consumer Discretionary',
  JPM: 'Financials', BAC: 'Financials', WFC: 'Financials', GS: 'Financials',
  MS: 'Financials', V: 'Financials', MA: 'Financials', AXP: 'Financials',
  BLK: 'Financials', SCHW: 'Financials', C: 'Financials',
  UNH: 'Healthcare', JNJ: 'Healthcare', LLY: 'Healthcare', PFE: 'Healthcare',
  ABBV: 'Healthcare', MRK: 'Healthcare', TMO: 'Healthcare', ABT: 'Healthcare',
  BA: 'Industrials', CAT: 'Industrials', HON: 'Industrials', UPS: 'Industrials',
  GE: 'Industrials', RTX: 'Industrials', LMT: 'Industrials', DE: 'Industrials',
  XOM: 'Energy', CVX: 'Energy', COP: 'Energy', SLB: 'Energy',
  EOG: 'Energy', OXY: 'Energy',
  PG: 'Consumer Staples', KO: 'Consumer Staples', PEP: 'Consumer Staples',
  COST: 'Consumer Staples', WMT: 'Consumer Staples', PM: 'Consumer Staples',
  T: 'Communication Services', VZ: 'Communication Services', TMUS: 'Communication Services',
  AMT: 'Real Estate', PLD: 'Real Estate', CCI: 'Real Estate',
  LIN: 'Materials', APD: 'Materials', FCX: 'Materials',
  NEE: 'Utilities', DUK: 'Utilities', SO: 'Utilities',
  XLF: 'Financials', XLE: 'Energy', XLK: 'Technology', XLV: 'Healthcare',
  XLI: 'Industrials', XLP: 'Consumer Staples', XLY: 'Consumer Discretionary',
  XLU: 'Utilities', XLB: 'Materials', XLRE: 'Real Estate',
  SPY: 'Broad Market', QQQ: 'Broad Market', IWM: 'Broad Market',
  DIA: 'Broad Market', VOO: 'Broad Market', VTI: 'Broad Market',
  GLD: 'Commodities', SLV: 'Commodities', USO: 'Commodities',
  TLT: 'Fixed Income', AGG: 'Fixed Income', HYG: 'Fixed Income',
}

function numeric(value, fallback = 0) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function localDateKey(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatDateKey(dateKey) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey)
  if (!match) return dateKey
  const [, year, month, day] = match
  return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

export function getAccountValue(account) {
  const eq = account?.equity || {}
  return numeric(eq.liquidationValue ?? eq.totalAccountValue ?? eq.equity)
}

export function getAccountCash(account) {
  const eq = account?.equity || {}
  return numeric(eq.cashBalance ?? eq.availableFunds)
}

function getUnderlying(symbol = '') {
  const trimmed = symbol.trim()
  if (trimmed.length >= 15 && /\d{6}[CP]\d{8}$/.test(trimmed)) {
    return trimmed.slice(0, 6).trim()
  }
  return trimmed
}

export function getSector(symbol) {
  return SECTOR_MAP[getUnderlying(symbol)] || 'Other'
}

export function getContractMultiplier(position) {
  const explicit = numeric(position?.contract_multiplier ?? position?.contractMultiplier, null)
  if (explicit && explicit > 0) return explicit
  return position?.instrument_type === 'OPTION' ? 100 : 1
}

export function computePositionDayPnl(position) {
  const prevClose = numeric(position?.prev_close_price)
  const qty = numeric(position?.quantity)
  const marketValue = numeric(position?.market_value)
  if (prevClose > 0 && qty !== 0) {
    return marketValue - prevClose * qty * getContractMultiplier(position)
  }
  return numeric(position?.current_day_pnl)
}

export function computePriorClosePct(dayChange, currentValue) {
  const priorClose = numeric(currentValue) - numeric(dayChange)
  return priorClose > 0 ? (numeric(dayChange) / priorClose) * 100 : 0
}

export function summarizeDayChange(accounts, positions) {
  const byAccount = {}
  for (const position of positions || []) {
    const accountNumber = position.account_number
    byAccount[accountNumber] = (byAccount[accountNumber] || 0) + computePositionDayPnl(position)
  }

  const totalValue = (accounts || []).reduce((sum, account) => sum + getAccountValue(account), 0)
  const totalDayChange = Object.values(byAccount).reduce((sum, value) => sum + value, 0)

  return {
    byAccount,
    totalDayChange,
    totalDayPct: computePriorClosePct(totalDayChange, totalValue),
  }
}

export function buildPositionGroups(positions) {
  const bySymbol = {}

  for (const pos of positions || []) {
    if (!bySymbol[pos.symbol]) {
      bySymbol[pos.symbol] = {
        symbol: pos.symbol,
        description: pos.raw?.instrument?.description || '',
        instrumentType: pos.instrument_type,
        accounts: [],
        totalQty: 0,
        totalMktVal: 0,
        totalDayPnl: 0,
        totalCostBasis: 0,
      }
    }

    const group = bySymbol[pos.symbol]
    const qty = numeric(pos.quantity)
    const mktVal = numeric(pos.market_value)
    const avgCost = numeric(pos.average_price)
    const { costBasis, gainLoss, gainLossPct } = derivePositionPnl(pos)
    const dayPnl = computePositionDayPnl(pos)

    group.accounts.push({
      accountNumber: pos.account_number,
      qty,
      mktVal,
      dayPnl,
      avgCost,
      costBasis,
      gainLoss,
      gainLossPct,
      prevClosePrice: numeric(pos.prev_close_price),
      cost_basis_long: pos.cost_basis_long,
      cost_basis_short: pos.cost_basis_short,
      unrealized_pnl_long: pos.unrealized_pnl_long,
      unrealized_pnl_short: pos.unrealized_pnl_short,
      cost_basis_source: pos.cost_basis_source,
      currency: pos.currency,
      contract_multiplier: pos.contract_multiplier,
      average_price: pos.average_price,
      instrument_type: pos.instrument_type,
      market_value: mktVal,
      quantity: qty,
    })

    group.totalQty += qty
    group.totalMktVal += mktVal
    group.totalDayPnl += dayPnl
    group.totalCostBasis += costBasis
  }

  const consolidated = Object.values(bySymbol).map(group => {
    group.accounts.sort((a, b) => b.mktVal - a.mktVal)
    const multiplier = group.instrumentType === 'OPTION' ? 100 : 1
    const prevMktVal = group.totalMktVal - group.totalDayPnl
    const gainLoss = group.totalMktVal - group.totalCostBasis
    return {
      ...group,
      price: group.totalQty > 0 ? group.totalMktVal / (group.totalQty * multiplier) : 0,
      priceChange: group.totalQty > 0 ? group.totalDayPnl / (group.totalQty * multiplier) : 0,
      priceChangePct: prevMktVal > 0 ? (group.totalDayPnl / prevMktVal) * 100 : 0,
      dayChangePct: prevMktVal > 0 ? (group.totalDayPnl / prevMktVal) * 100 : 0,
      gainLoss,
      gainLossPct: group.totalCostBasis > 0 ? (gainLoss / group.totalCostBasis) * 100 : 0,
      isMulti: group.accounts.length > 1,
    }
  })

  const groups = []
  const used = new Set()

  for (const def of INSTRUMENT_GROUPS) {
    const items = consolidated
      .filter(group => def.types.includes(group.instrumentType))
      .sort((a, b) => b.totalMktVal - a.totalMktVal)
    if (!items.length) continue
    items.forEach(group => used.add(group.symbol))
    groups.push(buildInstrumentGroup(def, items))
  }

  const others = consolidated.filter(group => !used.has(group.symbol)).sort((a, b) => b.totalMktVal - a.totalMktVal)
  if (others.length) {
    groups.push(buildInstrumentGroup({ key: 'other', label: 'Other' }, others))
  }

  return groups
}

function buildInstrumentGroup(def, positions) {
  const totalMktVal = positions.reduce((sum, position) => sum + position.totalMktVal, 0)
  const totalDayPnl = positions.reduce((sum, position) => sum + position.totalDayPnl, 0)
  const totalCostBasis = positions.reduce((sum, position) => sum + position.totalCostBasis, 0)
  const totalGainLoss = totalMktVal - totalCostBasis
  const prevTotal = totalMktVal - totalDayPnl
  return {
    ...def,
    positions,
    totalMktVal,
    totalDayPnl,
    totalCostBasis,
    totalGainLoss,
    dayChangePct: prevTotal > 0 ? (totalDayPnl / prevTotal) * 100 : 0,
    gainLossPct: totalCostBasis > 0 ? (totalGainLoss / totalCostBasis) * 100 : 0,
  }
}

export function buildPortfolioAllocation(accounts, positions, getNickname = accountNumber => `...${String(accountNumber).slice(-4)}`) {
  const groups = buildPositionGroups(positions)
  const allConsolidated = groups.flatMap(group => group.positions)
  const totalMktVal = groups.reduce((sum, group) => sum + group.totalMktVal, 0)
  const totalDayPnl = groups.reduce((sum, group) => sum + group.totalDayPnl, 0)
  const totalCostBasis = groups.reduce((sum, group) => sum + group.totalCostBasis, 0)
  const totalGainLoss = totalMktVal - totalCostBasis
  const accountValue = (accounts || []).reduce((sum, account) => sum + getAccountValue(account), 0)
  const totalCash = (accounts || []).reduce((sum, account) => sum + getAccountCash(account), 0)
  const portfolioValue = accountValue || totalMktVal
  const accountColorMap = new Map((accounts || []).map((account, index) => [account.account_number, CHART_COLORS[index % CHART_COLORS.length]]))

  const symbolColorMap = new Map(
    Object.entries((positions || []).reduce((acc, position) => {
      acc[position.symbol] = (acc[position.symbol] || 0) + numeric(position.market_value)
      return acc
    }, {}))
      .sort((a, b) => b[1] - a[1])
      .map(([symbol], index) => [symbol, CHART_COLORS[index % CHART_COLORS.length]])
  )

  const typeChartData = groups.map((group, index) => ({
    name: group.label,
    value: group.totalMktVal,
    pct: portfolioValue > 0 ? (group.totalMktVal / portfolioValue) * 100 : 0,
    count: group.positions.length,
    color: CHART_COLORS[index % CHART_COLORS.length],
  }))

  const sortedHoldings = [...allConsolidated].sort((a, b) => b.totalMktVal - a.totalMktVal)
  const topHoldings = sortedHoldings.slice(0, 8)
  const restHoldings = sortedHoldings.slice(8)
  const restTotal = restHoldings.reduce((sum, position) => sum + position.totalMktVal, 0)
  const topHoldingsData = topHoldings.map(position => ({
    name: position.symbol,
    value: position.totalMktVal,
    pct: portfolioValue > 0 ? (position.totalMktVal / portfolioValue) * 100 : 0,
    acctCount: position.accounts.length,
    color: symbolColorMap.get(position.symbol),
  }))
  if (restTotal > 0) {
    topHoldingsData.push({
      name: 'Other',
      value: restTotal,
      pct: portfolioValue > 0 ? (restTotal / portfolioValue) * 100 : 0,
      acctCount: new Set(restHoldings.flatMap(position => position.accounts.map(account => account.accountNumber))).size,
      color: '#8a92a0',
    })
  }

  const accountChartData = (accounts || []).map(account => {
    const value = getAccountValue(account)
    const posCount = (positions || []).filter(position => position.account_number === account.account_number).length
    return {
      name: getNickname(account.account_number),
      value,
      pct: accountValue > 0 ? (value / accountValue) * 100 : 0,
      posCount,
      color: accountColorMap.get(account.account_number),
    }
  })

  const concentrationData = sortedHoldings.map(position => ({
    symbol: position.symbol,
    pct: portfolioValue > 0 ? (position.totalMktVal / portfolioValue) * 100 : 0,
    pctLabel: portfolioValue > 0 ? ((position.totalMktVal / portfolioValue) * 100).toFixed(1) + '%' : '0%',
  }))

  const sectorTotals = {}
  for (const position of allConsolidated) {
    const sector = getSector(position.symbol)
    if (!sectorTotals[sector]) sectorTotals[sector] = { value: 0, count: 0 }
    sectorTotals[sector].value += position.totalMktVal
    sectorTotals[sector].count += 1
  }

  const sectorChartData = Object.entries(sectorTotals)
    .sort((a, b) => b[1].value - a[1].value)
    .map(([name, data], index) => ({
      name,
      value: data.value,
      pct: portfolioValue > 0 ? (data.value / portfolioValue) * 100 : 0,
      count: data.count,
      color: CHART_COLORS[index % CHART_COLORS.length],
    }))

  const invested = accountValue - totalCash
  const cashVsInvestedData = accountValue === 0
    ? []
    : [
      { name: 'Cash', value: totalCash, pct: (totalCash / accountValue) * 100, color: '#5b7ea1' },
      { name: 'Invested', value: invested, pct: (invested / accountValue) * 100, color: '#4a6741' },
    ]

  return {
    groups,
    allConsolidated,
    totalMktVal,
    totalDayPnl,
    totalDayPct: computePriorClosePct(totalDayPnl, accountValue || totalMktVal),
    totalCostBasis,
    totalGainLoss,
    totalGainLossPct: totalCostBasis > 0 ? (totalGainLoss / totalCostBasis) * 100 : 0,
    accountValue,
    totalCash,
    invested,
    portfolioValue,
    typeChartData,
    topHoldingsData,
    accountChartData,
    concentrationData,
    sectorChartData,
    cashVsInvestedData,
  }
}

export function buildEquityCurveData(snapshots, currentEquity, now = new Date()) {
  const todayKey = localDateKey(now)
  const rowsByDate = new Map()

  for (const snapshot of snapshots || []) {
    const dateKey = String(snapshot.snapshot_date || '').slice(0, 10)
    if (!dateKey) continue
    rowsByDate.set(dateKey, {
      date: formatDateKey(dateKey),
      dateKey,
      equity: numeric(snapshot.total_equity),
    })
  }

  if (numeric(currentEquity) > 0) {
    rowsByDate.set(todayKey, {
      date: formatDateKey(todayKey),
      dateKey: todayKey,
      equity: numeric(currentEquity),
    })
  }

  return [...rowsByDate.values()].sort((a, b) => a.dateKey.localeCompare(b.dateKey))
}
